/**
 * @fileoverview Browser environment helpers.
 *
 * Tiny utilities to check for browser globals safely so feature classes can
 * expose `autoTrack()` methods that no-op gracefully on the server.
 */

/** Safe `window` accessor; returns `undefined` outside the browser. */
export function getWindow(): (Window & typeof globalThis) | undefined {
  return typeof window !== "undefined" ? window : undefined;
}

/** Safe `document` accessor; returns `undefined` outside the browser. */
export function getDocument(): Document | undefined {
  return typeof document !== "undefined" ? document : undefined;
}

/** Returns true when running in a real browser-like environment. */
export function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/** A teardown function returned by every `autoTrack()` API. */
export type Teardown = () => void;

/** Compose multiple teardown functions into one. */
export function combineTeardowns(...fns: Array<Teardown | undefined | null | false>): Teardown {
  return () => {
    for (const fn of fns) {
      if (typeof fn === "function") {
        try { fn(); } catch { /* swallow */ }
      }
    }
  };
}
